import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config/constants';
import { DIFFICULTY_PRESETS, getDifficulty, setDifficulty, type DifficultyLevel } from '../config/difficultySettings';
import { getPlayers, getCurrentPlayer, getCurrentPlayerIndex, setCurrentPlayer, renamePlayer, resetPlayerStats } from '../config/playerManager';

export class HomeScene extends Phaser.Scene {
  private starField!: Phaser.GameObjects.Graphics;
  private stars: { x: number; y: number; size: number; alpha: number; speed: number }[] = [];
  private difficultyButtons: Map<DifficultyLevel, { bg: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text }> = new Map();
  private playerButtons: { bg: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text }[] = [];
  private titleGlow!: Phaser.GameObjects.Text;
  private helpModal?: Phaser.GameObjects.Container;
  private nameInputModal?: Phaser.GameObjects.Container;
  private settingsModal?: Phaser.GameObjects.Container;
  private playerStatsText!: Phaser.GameObjects.Text;
  private editingPlayerIndex: number = -1;
  private currentInputName: string = '';

  constructor() {
    super({ key: 'HomeScene' });
  }

  create(): void {
    // Reset state for clean re-entry
    this.playerButtons = [];
    this.difficultyButtons.clear();
    this.helpModal = undefined;
    this.nameInputModal = undefined;
    this.settingsModal = undefined;
    this.editingPlayerIndex = -1;
    this.currentInputName = '';

    // Create animated star field background
    this.createStarField();
    
    // Create neon border effect
    this.createNeonBorder();

    // Title glow layer (behind)
    this.titleGlow = this.add.text(GAME_WIDTH / 2, 100, 'ROLLER TOASTER', {
      fontFamily: 'Arial Black',
      fontSize: '54px',
      color: '#ff6b35',
    }).setOrigin(0.5).setAlpha(0.5);

    // Title with neon effect
    const title = this.add.text(GAME_WIDTH / 2, 100, 'ROLLER TOASTER', {
      fontFamily: 'Arial Black',
      fontSize: '52px',
      color: '#ffff00',
      stroke: '#ff6b35',
      strokeThickness: 4,
    });
    title.setOrigin(0.5);

    // Animate title glow pulsing
    this.tweens.add({
      targets: [title, this.titleGlow],
      scaleX: { from: 1, to: 1.02 },
      scaleY: { from: 1, to: 1.02 },
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.tweens.add({
      targets: this.titleGlow,
      alpha: { from: 0.3, to: 0.6 },
      duration: 600,
      yoyo: true,
      repeat: -1,
    });

    // Animated truck driving on top of title
    this.createTitleTruck();

    // Subtitle with typewriter effect
    const subtitleText = 'A Space Invaders-Inspired Arcade Game';
    const subtitle = this.add.text(GAME_WIDTH / 2, 160, '', {
      fontFamily: 'Arial',
      fontSize: '16px',
      color: '#00ffff',
    }).setOrigin(0.5);

    // Typewriter animation
    let charIndex = 0;
    this.time.addEvent({
      delay: 40,
      repeat: subtitleText.length - 1,
      callback: () => {
        charIndex++;
        subtitle.setText(subtitleText.substring(0, charIndex));
      },
    });

    // Animated truck display with wheel glow
    const truckContainer = this.add.container(GAME_WIDTH / 2, 250);
    
    // Wheel glow effects
    const leftGlow = this.add.sprite(-40, 44, 'wheelGlow').setScale(2.5).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.6);
    const rightGlow = this.add.sprite(40, 44, 'wheelGlow').setScale(2.5).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.6);
    
    const truck = this.add.sprite(0, 0, 'truck').setScale(2);
    
    truckContainer.add([leftGlow, rightGlow, truck]);
    
    // Animate wheel glow
    this.tweens.add({
      targets: [leftGlow, rightGlow],
      alpha: { from: 0.4, to: 0.8 },
      scale: { from: 2.2, to: 2.8 },
      duration: 400,
      yoyo: true,
      repeat: -1,
    });
    
    // Truck bobbing animation
    this.tweens.add({
      targets: truckContainer,
      y: truckContainer.y + 8,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Player selection
    this.add.text(GAME_WIDTH / 2, 320, 'SELECT PLAYER', {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#888888',
    }).setOrigin(0.5);

    this.createPlayerSelector();

    // Player stats display
    this.playerStatsText = this.add.text(GAME_WIDTH / 2, 390, '', {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#ff6b35',
      align: 'center',
    }).setOrigin(0.5);
    this.updatePlayerStats();

    // Difficulty selection
    this.add.text(GAME_WIDTH / 2, 430, 'DIFFICULTY', {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#888888',
    }).setOrigin(0.5);

    this.createDifficultySelector();

    // Play button
    this.createButton(GAME_WIDTH / 2, 520, 'PLAY GAME', 0x00ff00, () => {
      this.scene.start('GameScene');
    });

    // Help button (bottom left)
    this.createSmallButton(85, GAME_HEIGHT - 40, '? HELP', 0x00ffff, () => {
      this.showHelpModal();
    });

    // Close button (bottom left, right of help)
    this.createSmallButton(185, GAME_HEIGHT - 40, '✕ CLOSE', 0xff3333, () => {
      this.scene.start('SplashScene');
    });

    // Settings button (bottom right)
    this.createSmallButton(GAME_WIDTH - 85, GAME_HEIGHT - 40, '⚙ OPTIONS', 0xff6b35, () => {
      this.showSettingsModal();
    });

    // Controls hint (bottom center)
    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 45, '← → move  |  SPACE fire  |  ESC pause', {
      fontFamily: 'Arial',
      fontSize: '10px',
      color: '#ffffff',
    }).setOrigin(0.5);

    // Game authors credit
    const creditText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 32, 'by \u00A9Affordable Web Designs', {
      fontFamily: 'Arial',
      fontSize: '11px',
      color: '#ff0000',
    }).setOrigin(0.5);
    creditText.setInteractive({ useHandCursor: true });
    creditText.on('pointerdown', () => {
      window.open('https://www.affordablewebdesigns.co', '_blank', 'noopener');
    });
    creditText.on('pointerover', () => {
      creditText.setColor('#ff4444');
    });
    creditText.on('pointerout', () => {
      creditText.setColor('#ff0000');
    });
    this.tweens.add({
      targets: creditText,
      alpha: { from: 0.5, to: 1 },
      duration: 1400,
      yoyo: true,
      repeat: -1,
    });
  }

  update(): void {
    this.updateStarField();
  }

  private createButton(x: number, y: number, text: string, color: number, onClick: () => void): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const bg = this.add.rectangle(0, 0, 250, 55, 0x222222)
      .setStrokeStyle(3, color);
    
    const label = this.add.text(0, 0, text, {
      fontFamily: 'Arial Black',
      fontSize: '24px',
      color: '#ffffff',
    }).setOrigin(0.5);

    container.add([bg, label]);

    bg.setInteractive({ useHandCursor: true });
    
    bg.on('pointerover', () => {
      bg.setFillStyle(0x333333);
      bg.setStrokeStyle(4, 0xffff00);
      label.setColor('#ffff00');
    });

    bg.on('pointerout', () => {
      bg.setFillStyle(0x222222);
      bg.setStrokeStyle(3, color);
      label.setColor('#ffffff');
    });

    bg.on('pointerdown', onClick);

    return container;
  }

  private createPlayerSelector(): void {
    const players = getPlayers();
    const buttonWidth = 120;
    const spacing = 10;
    const totalWidth = players.length * buttonWidth + (players.length - 1) * spacing;
    const startX = (GAME_WIDTH - totalWidth) / 2 + buttonWidth / 2;
    const y = 355;

    // Hint text (below player buttons)
    this.add.text(GAME_WIDTH / 2, 380, '(double-click to edit name)', {
      fontFamily: 'Arial',
      fontSize: '10px',
      color: '#666666',
    }).setOrigin(0.5);

    players.forEach((player, index) => {
      const x = startX + index * (buttonWidth + spacing);

      const bg = this.add.rectangle(x, y, buttonWidth, 35, 0x222222)
        .setStrokeStyle(2, 0x00ffff);
      
      const label = this.add.text(x, y, player.name, {
        fontFamily: 'Arial Black',
        fontSize: '12px',
        color: '#ffffff',
      }).setOrigin(0.5);

      this.playerButtons.push({ bg, label });

      bg.setInteractive({ useHandCursor: true });
      
      // Track clicks for double-click detection
      let lastClickTime = 0;
      
      bg.on('pointerover', () => {
        if (getCurrentPlayerIndex() !== index) {
          bg.setFillStyle(0x333333);
        }
      });

      bg.on('pointerout', () => {
        if (getCurrentPlayerIndex() !== index) {
          bg.setFillStyle(0x222222);
        }
      });

      bg.on('pointerdown', () => {
        const currentTime = this.time.now;
        
        // Check for double-click (within 300ms)
        if (currentTime - lastClickTime < 300) {
          // Double-click: open name editor
          this.showNameInputModal(index);
        } else {
          // Single click: select player
          setCurrentPlayer(index);
          this.updatePlayerSelection();
          this.updatePlayerStats();
        }
        
        lastClickTime = currentTime;
      });
    });

    this.updatePlayerSelection();
  }

  private updatePlayerSelection(): void {
    const currentIndex = getCurrentPlayerIndex();
    
    this.playerButtons.forEach((button, index) => {
      if (index === currentIndex) {
        button.bg.setFillStyle(0x00ffff);
        button.bg.setStrokeStyle(3, 0xffffff);
        button.label.setColor('#000000');
      } else {
        button.bg.setFillStyle(0x222222);
        button.bg.setStrokeStyle(2, 0x00ffff);
        button.label.setColor('#ffffff');
      }
    });
  }

  private updatePlayerStats(): void {
    const player = getCurrentPlayer();
    this.playerStatsText.setText(
      `HIGH SCORE: ${player.highScore}  |  BEST WAVE: ${player.bestWave}  |  GAMES: ${player.gamesPlayed}`
    );
  }

  private createDifficultySelector(): void {
    const difficulties: DifficultyLevel[] = ['beginner', 'experienced', 'expert'];
    const buttonWidth = 130;
    const spacing = 10;
    const totalWidth = difficulties.length * buttonWidth + (difficulties.length - 1) * spacing;
    const startX = (GAME_WIDTH - totalWidth) / 2 + buttonWidth / 2;

    difficulties.forEach((level, index) => {
      const preset = DIFFICULTY_PRESETS[level];
      const x = startX + index * (buttonWidth + spacing);
      const y = 465;

      const bg = this.add.rectangle(x, y, buttonWidth, 40, 0x222222)
        .setStrokeStyle(2, preset.color);
      
      const label = this.add.text(x, y, preset.name, {
        fontFamily: 'Arial Black',
        fontSize: '14px',
        color: '#ffffff',
      }).setOrigin(0.5);

      this.difficultyButtons.set(level, { bg, label });

      bg.setInteractive({ useHandCursor: true });
      
      bg.on('pointerover', () => {
        if (getDifficulty() !== level) {
          bg.setFillStyle(0x333333);
        }
      });

      bg.on('pointerout', () => {
        if (getDifficulty() !== level) {
          bg.setFillStyle(0x222222);
        }
      });

      bg.on('pointerdown', () => {
        setDifficulty(level);
        this.updateDifficultySelection();
      });
    });

    // Set initial selection
    this.updateDifficultySelection();
  }

  private updateDifficultySelection(): void {
    const current = getDifficulty();
    
    this.difficultyButtons.forEach((button, level) => {
      const preset = DIFFICULTY_PRESETS[level];
      
      if (level === current) {
        button.bg.setFillStyle(preset.color);
        button.bg.setStrokeStyle(3, 0xffffff);
        button.label.setColor('#000000');
      } else {
        button.bg.setFillStyle(0x222222);
        button.bg.setStrokeStyle(2, preset.color);
        button.label.setColor('#ffffff');
      }
    });
  }

  private createStarField(): void {
    this.starField = this.add.graphics();
    this.stars = [];
    
    for (let i = 0; i < 80; i++) {
      this.stars.push({
        x: Phaser.Math.Between(0, GAME_WIDTH),
        y: Phaser.Math.Between(0, GAME_HEIGHT),
        size: Phaser.Math.FloatBetween(0.5, 2),
        alpha: Phaser.Math.FloatBetween(0.2, 0.8),
        speed: Phaser.Math.FloatBetween(0.1, 0.5),
      });
    }
  }

  private updateStarField(): void {
    this.starField.clear();
    
    this.stars.forEach((star) => {
      // Twinkle
      star.alpha += Phaser.Math.FloatBetween(-0.02, 0.02);
      star.alpha = Phaser.Math.Clamp(star.alpha, 0.1, 1);
      
      // Slow drift down
      star.y += star.speed;
      if (star.y > GAME_HEIGHT) {
        star.y = 0;
        star.x = Phaser.Math.Between(0, GAME_WIDTH);
      }
      
      this.starField.fillStyle(0xffffff, star.alpha);
      this.starField.fillCircle(star.x, star.y, star.size);
    });
  }

  private createSmallButton(x: number, y: number, text: string, color: number, onClick: () => void): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const bg = this.add.rectangle(0, 0, 95, 30, 0x222222)
      .setStrokeStyle(2, color);
    
    const label = this.add.text(0, 0, text, {
      fontFamily: 'Arial Black',
      fontSize: '12px',
      color: '#ffffff',
    }).setOrigin(0.5);

    container.add([bg, label]);

    bg.setInteractive({ useHandCursor: true });
    
    bg.on('pointerover', () => {
      bg.setFillStyle(0x333333);
      bg.setStrokeStyle(3, 0xffff00);
      label.setColor('#ffff00');
    });

    bg.on('pointerout', () => {
      bg.setFillStyle(0x222222);
      bg.setStrokeStyle(2, color);
      label.setColor('#ffffff');
    });

    bg.on('pointerdown', onClick);

    return container;
  }

  private showHelpModal(): void {
    if (this.helpModal) {
      this.helpModal.setVisible(true);
      return;
    }

    this.helpModal = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2);
    this.helpModal.setDepth(100);

    // Dark overlay (add first - bottom layer)
    const overlay = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85);
    overlay.setInteractive(); // Blocks clicks behind

    // Modal background
    const modalBg = this.add.rectangle(0, 0, 600, 480, 0x111122)
      .setStrokeStyle(3, 0x00ffff);

    // Title
    const title = this.add.text(0, -210, 'HOW TO PLAY', {
      fontFamily: 'Arial Black',
      fontSize: '32px',
      color: '#ffff00',
    }).setOrigin(0.5);

    // Add base elements FIRST (so they're behind content)
    this.helpModal.add([overlay, modalBg, title]);

    // Content sections (add after base elements so they appear on top)
    const content = [
      { title: '🎯 OBJECTIVE', text: 'Defend Earth from waves of breakfast enemies!\nDestroy all enemies to complete each wave.', extraSpaceAfter: 0 },
      { title: '🎮 CONTROLS', text: '← → or A/D  -  Move truck left/right\nSPACE  -  Fire toast projectiles\nESC or P  -  Pause game', extraSpaceAfter: 0 },
      { title: '👾 ENEMIES', text: 'Evil Egg (10 pts) - Basic enemy\nAngry Bacon (20 pts) - Fast shooter\nMenacing Coffee (30 pts) - Tank, takes 2 hits\nClassic Alien (25 pts) - Retro invader', extraSpaceAfter: 15 },
      { title: '⚡ POWER-UPS', text: 'Rapid Fire (Orange) - 2.5x faster firing\nButter Shield (Yellow) - Absorbs 3 hits\nJam Spread (Pink) - Triple spread shot', extraSpaceAfter: 0 },
      { title: '🛡️ BARRIERS', text: 'Hide behind barriers for protection!\nBarriers take damage and can be destroyed.', extraSpaceAfter: 0 },
      { title: '🏆 SCORING', text: 'Wave Bonus: 500 × wave number\nPerfect Wave (no damage): +200 bonus\nHigher difficulty = higher score multiplier!', extraSpaceAfter: 0 },
    ];

    let yPos = -200;
    content.forEach((section) => {
      const sectionTitle = this.add.text(-270, yPos, section.title, {
        fontFamily: 'Arial Black',
        fontSize: '13px',
        color: '#00ffff',
      });
      
      const sectionText = this.add.text(-270, yPos + 16, section.text, {
        fontFamily: 'Arial',
        fontSize: '11px',
        color: '#cccccc',
        lineSpacing: 2,
      });

      this.helpModal!.add([sectionTitle, sectionText]);
      yPos += 70 + section.extraSpaceAfter;
    });

    // Close button (add last so it's on top) - right justified
    const closeBtn = this.add.rectangle(220, 210, 120, 36, 0x00aa00)
      .setStrokeStyle(2, 0x00ff00);
    const closeText = this.add.text(220, 210, 'GOT IT!', {
      fontFamily: 'Arial Black',
      fontSize: '16px',
      color: '#ffffff',
    }).setOrigin(0.5);

    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.on('pointerover', () => {
      closeBtn.setFillStyle(0x00cc00);
    });
    closeBtn.on('pointerout', () => {
      closeBtn.setFillStyle(0x00aa00);
    });
    closeBtn.on('pointerdown', () => {
      this.helpModal?.setVisible(false);
    });

    // Also close on overlay click
    overlay.on('pointerdown', () => {
      this.helpModal?.setVisible(false);
    });

    this.helpModal.add([closeBtn, closeText]);
  }

  private showNameInputModal(playerIndex: number): void {
    this.editingPlayerIndex = playerIndex;
    const players = getPlayers();
    this.currentInputName = players[playerIndex].name;

    // Destroy existing modal if any
    if (this.nameInputModal) {
      this.nameInputModal.destroy();
    }

    this.nameInputModal = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2);
    this.nameInputModal.setDepth(200);

    // Dark overlay
    const overlay = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85);
    overlay.setInteractive();

    // Modal background
    const modalBg = this.add.rectangle(0, 0, 400, 220, 0x111122)
      .setStrokeStyle(3, 0x00ffff);

    // Title
    const title = this.add.text(0, -80, 'ENTER PLAYER NAME', {
      fontFamily: 'Arial Black',
      fontSize: '24px',
      color: '#00ffff',
    }).setOrigin(0.5);

    // Input display box
    const inputBg = this.add.rectangle(0, -20, 300, 50, 0x000000)
      .setStrokeStyle(2, 0xffff00);

    // Current name text (editable display)
    const nameDisplay = this.add.text(0, -20, this.currentInputName, {
      fontFamily: 'Arial Black',
      fontSize: '28px',
      color: '#ffff00',
    }).setOrigin(0.5);

    // Blinking cursor
    const cursor = this.add.text(0, -20, '_', {
      fontFamily: 'Arial Black',
      fontSize: '28px',
      color: '#ffff00',
    }).setOrigin(0, 0.5);

    // Animate cursor blink
    this.tweens.add({
      targets: cursor,
      alpha: { from: 1, to: 0 },
      duration: 500,
      yoyo: true,
      repeat: -1,
    });

    // Update cursor position
    const updateCursor = () => {
      const textWidth = nameDisplay.width;
      cursor.setX(textWidth / 2 + 2);
    };
    updateCursor();

    // Instructions
    const instructions = this.add.text(0, 30, 'Type name (max 12 chars) • ENTER to save • ESC to cancel', {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#888888',
    }).setOrigin(0.5);

    // Save button
    const saveBtn = this.add.rectangle(-70, 80, 120, 40, 0x00aa00)
      .setStrokeStyle(2, 0x00ff00);
    const saveText = this.add.text(-70, 80, 'SAVE', {
      fontFamily: 'Arial Black',
      fontSize: '16px',
      color: '#ffffff',
    }).setOrigin(0.5);

    saveBtn.setInteractive({ useHandCursor: true });
    saveBtn.on('pointerover', () => saveBtn.setFillStyle(0x00cc00));
    saveBtn.on('pointerout', () => saveBtn.setFillStyle(0x00aa00));
    saveBtn.on('pointerdown', () => this.savePlayerName());

    // Cancel button
    const cancelBtn = this.add.rectangle(70, 80, 120, 40, 0xaa0000)
      .setStrokeStyle(2, 0xff0000);
    const cancelText = this.add.text(70, 80, 'CANCEL', {
      fontFamily: 'Arial Black',
      fontSize: '16px',
      color: '#ffffff',
    }).setOrigin(0.5);

    cancelBtn.setInteractive({ useHandCursor: true });
    cancelBtn.on('pointerover', () => cancelBtn.setFillStyle(0xcc0000));
    cancelBtn.on('pointerout', () => cancelBtn.setFillStyle(0xaa0000));
    cancelBtn.on('pointerdown', () => this.closeNameInputModal());

    // Close on overlay click
    overlay.on('pointerdown', () => this.closeNameInputModal());

    this.nameInputModal.add([
      overlay, modalBg, title, inputBg, nameDisplay, cursor,
      instructions, saveBtn, saveText, cancelBtn, cancelText
    ]);

    // Store references for keyboard handling
    this.nameInputModal.setData('nameDisplay', nameDisplay);
    this.nameInputModal.setData('cursor', cursor);
    this.nameInputModal.setData('updateCursor', updateCursor);

    // Set up keyboard input
    this.setupNameInput();
  }

  private setupNameInput(): void {
    // Remove any existing listeners
    this.input.keyboard?.removeAllListeners('keydown');

    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (!this.nameInputModal || !this.nameInputModal.visible) return;

      const nameDisplay = this.nameInputModal.getData('nameDisplay') as Phaser.GameObjects.Text;
      const updateCursor = this.nameInputModal.getData('updateCursor') as () => void;

      if (event.key === 'Enter') {
        this.savePlayerName();
      } else if (event.key === 'Escape') {
        this.closeNameInputModal();
      } else if (event.key === 'Backspace') {
        if (this.currentInputName.length > 0) {
          this.currentInputName = this.currentInputName.slice(0, -1);
          nameDisplay.setText(this.currentInputName || ' ');
          updateCursor();
        }
      } else if (event.key.length === 1 && this.currentInputName.length < 12) {
        // Only allow alphanumeric and some special characters
        if (/^[a-zA-Z0-9 _-]$/.test(event.key)) {
          this.currentInputName += event.key.toUpperCase();
          nameDisplay.setText(this.currentInputName);
          updateCursor();
        }
      }
    });
  }

  private savePlayerName(): void {
    if (this.currentInputName.trim().length > 0 && this.editingPlayerIndex >= 0) {
      renamePlayer(this.editingPlayerIndex, this.currentInputName.trim());
      
      // Update the button label
      if (this.playerButtons[this.editingPlayerIndex]) {
        this.playerButtons[this.editingPlayerIndex].label.setText(this.currentInputName.trim().toUpperCase());
      }
      
      this.updatePlayerStats();
    }
    this.closeNameInputModal();
  }

  private closeNameInputModal(): void {
    if (this.nameInputModal) {
      this.nameInputModal.destroy();
      this.nameInputModal = undefined;
    }
    this.editingPlayerIndex = -1;
    this.currentInputName = '';
    
    // Remove keyboard listeners
    this.input.keyboard?.removeAllListeners('keydown');
  }

  private showSettingsModal(): void {
    if (this.settingsModal) {
      this.settingsModal.setVisible(true);
      this.refreshSettingsModal();
      return;
    }

    this.settingsModal = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2);
    this.settingsModal.setDepth(100);

    // Dark overlay
    const overlay = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.9);
    overlay.setInteractive();

    // Modal background
    const modalBg = this.add.rectangle(0, 0, 650, 500, 0x111122)
      .setStrokeStyle(3, 0xff6b35);

    // Title
    const title = this.add.text(0, -220, '⚙ GAME OPTIONS', {
      fontFamily: 'Arial Black',
      fontSize: '32px',
      color: '#ff6b35',
    }).setOrigin(0.5);

    this.settingsModal.add([overlay, modalBg, title]);

    // === SECTION 1: DIFFICULTY ===
    this.addSettingsSection(-280, -160, 'DIFFICULTY', () => {
      const difficulties: DifficultyLevel[] = ['beginner', 'experienced', 'expert'];
      const container = this.add.container(0, 0);
      
      difficulties.forEach((level, index) => {
        const preset = DIFFICULTY_PRESETS[level];
        const x = -100 + index * 100;
        const y = 0;
        
        const btn = this.add.rectangle(x, y, 90, 35, 0x222222)
          .setStrokeStyle(2, preset.color);
        const label = this.add.text(x, y, preset.name, {
          fontFamily: 'Arial Black',
          fontSize: '11px',
          color: '#ffffff',
        }).setOrigin(0.5);
        
        btn.setInteractive({ useHandCursor: true });
        btn.on('pointerdown', () => {
          setDifficulty(level);
          this.refreshSettingsModal();
          this.updateDifficultySelection();
        });

        // Highlight current
        if (getDifficulty() === level) {
          btn.setFillStyle(preset.color);
          label.setColor('#000000');
        }

        container.add([btn, label]);
      });

      // Difficulty description
      const desc = this.getDifficultyDescription(getDifficulty());
      const descText = this.add.text(0, 35, desc, {
        fontFamily: 'Arial',
        fontSize: '11px',
        color: '#888888',
        align: 'center',
      }).setOrigin(0.5);
      container.add(descText);

      return container;
    });

    // === SECTION 2: PLAYER MANAGEMENT ===
    this.addSettingsSection(-250, -50, 'PLAYER MANAGEMENT', () => {
      const container = this.add.container(0, 0);
      const players = getPlayers();
      const currentIndex = getCurrentPlayerIndex();
      
      players.forEach((player, index) => {
        const y = index * 40;
        const isSelected = index === currentIndex;
        
        // Player name button
        const nameBg = this.add.rectangle(-70, y, 150, 30, isSelected ? 0x00ffff : 0x222222)
          .setStrokeStyle(2, 0x00ffff);
        const nameLabel = this.add.text(-70, y, player.name, {
          fontFamily: 'Arial Black',
          fontSize: '12px',
          color: isSelected ? '#000000' : '#ffffff',
        }).setOrigin(0.5);

        nameBg.setInteractive({ useHandCursor: true });
        nameBg.on('pointerdown', () => {
          setCurrentPlayer(index);
          this.refreshSettingsModal();
          this.updatePlayerSelection();
          this.updatePlayerStats();
        });

        // Edit name button
        const editBtn = this.add.rectangle(45, y, 60, 30, 0x333333)
          .setStrokeStyle(1, 0xffff00);
        const editLabel = this.add.text(45, y, 'EDIT', {
          fontFamily: 'Arial',
          fontSize: '10px',
          color: '#ffff00',
        }).setOrigin(0.5);

        editBtn.setInteractive({ useHandCursor: true });
        editBtn.on('pointerdown', () => {
          this.settingsModal?.setVisible(false);
          this.showNameInputModal(index);
        });

        // Reset stats button
        const resetBtn = this.add.rectangle(115, y, 60, 30, 0x333333)
          .setStrokeStyle(1, 0xff4444);
        const resetLabel = this.add.text(115, y, 'RESET', {
          fontFamily: 'Arial',
          fontSize: '10px',
          color: '#ff4444',
        }).setOrigin(0.5);

        resetBtn.setInteractive({ useHandCursor: true });
        resetBtn.on('pointerdown', () => {
          this.showConfirmReset(index);
        });

        // Stats display
        const stats = this.add.text(170, y, `HI: ${player.highScore} | W: ${player.bestWave} | G: ${player.gamesPlayed}`, {
          fontFamily: 'Arial',
          fontSize: '10px',
          color: '#888888',
        }).setOrigin(0, 0.5);

        container.add([nameBg, nameLabel, editBtn, editLabel, resetBtn, resetLabel, stats]);
      });

      return container;
    });

    // === SECTION 3: GAME INFO ===
    this.addSettingsSection(-280, 110, 'GAME INFO', () => {
      const container = this.add.container(0, 0);
      
      const info = [
        'Roller Toaster v1.0',
        'A Space Invaders-Inspired Arcade Game',
        '',
        'Built with Phaser 3 + TypeScript',
        'Waves to Win: 3',
      ];

      info.forEach((line, index) => {
        const text = this.add.text(0, index * 18, line, {
          fontFamily: 'Arial',
          fontSize: '12px',
          color: index === 0 ? '#ffff00' : '#888888',
        }).setOrigin(0, 0);
        container.add(text);
      });

      return container;
    });

    // Close button (right justified)
    const closeBtn = this.add.rectangle(250, 210, 120, 40, 0x00aa00)
      .setStrokeStyle(2, 0x00ff00);
    const closeText = this.add.text(250, 210, 'CLOSE', {
      fontFamily: 'Arial Black',
      fontSize: '16px',
      color: '#ffffff',
    }).setOrigin(0.5);

    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.on('pointerover', () => closeBtn.setFillStyle(0x00cc00));
    closeBtn.on('pointerout', () => closeBtn.setFillStyle(0x00aa00));
    closeBtn.on('pointerdown', () => this.settingsModal?.setVisible(false));

    overlay.on('pointerdown', () => this.settingsModal?.setVisible(false));

    this.settingsModal.add([closeBtn, closeText]);
  }

  private addSettingsSection(
    x: number,
    y: number,
    title: string,
    contentBuilder: () => Phaser.GameObjects.Container
  ): void {
    if (!this.settingsModal) return;

    // Section title
    const titleText = this.add.text(x, y, title, {
      fontFamily: 'Arial Black',
      fontSize: '14px',
      color: '#00ffff',
    });
    this.settingsModal.add(titleText);

    // Section content
    const content = contentBuilder();
    content.setPosition(x + 120, y + 35);
    this.settingsModal.add(content);

    // Store for refresh
    content.setData('builder', contentBuilder);
    content.setData('baseX', x + 120);
    content.setData('baseY', y + 35);
  }

  private refreshSettingsModal(): void {
    if (!this.settingsModal) return;

    // Rebuild the modal
    this.settingsModal.destroy();
    this.settingsModal = undefined;
    this.showSettingsModal();
  }

  private getDifficultyDescription(level: DifficultyLevel): string {
    const preset = DIFFICULTY_PRESETS[level];
    return `Lives: ${preset.lives} | Barriers: ${preset.barrierCount} | Score: ${preset.scoreMultiplier}x\nPower-up Drop: ${Math.round(preset.powerUpDropRate * 100)}%`;
  }

  private showConfirmReset(playerIndex: number): void {
    const players = getPlayers();
    const playerName = players[playerIndex].name;

    const confirm = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2);
    confirm.setDepth(300);

    const bg = this.add.rectangle(0, 0, 350, 150, 0x222222)
      .setStrokeStyle(3, 0xff4444);
    
    const msg = this.add.text(0, -40, `Reset ${playerName}'s stats?`, {
      fontFamily: 'Arial Black',
      fontSize: '20px',
      color: '#ffffff',
    }).setOrigin(0.5);

    const warning = this.add.text(0, -10, 'This cannot be undone!', {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#ff6666',
    }).setOrigin(0.5);

    const yesBtn = this.add.rectangle(-60, 40, 100, 35, 0xaa0000)
      .setStrokeStyle(2, 0xff0000);
    const yesText = this.add.text(-60, 40, 'RESET', {
      fontFamily: 'Arial Black',
      fontSize: '14px',
      color: '#ffffff',
    }).setOrigin(0.5);

    yesBtn.setInteractive({ useHandCursor: true });
    yesBtn.on('pointerdown', () => {
      resetPlayerStats(playerIndex);
      confirm.destroy();
      this.refreshSettingsModal();
      this.updatePlayerStats();
    });

    const noBtn = this.add.rectangle(60, 40, 100, 35, 0x444444)
      .setStrokeStyle(2, 0x888888);
    const noText = this.add.text(60, 40, 'CANCEL', {
      fontFamily: 'Arial Black',
      fontSize: '14px',
      color: '#ffffff',
    }).setOrigin(0.5);

    noBtn.setInteractive({ useHandCursor: true });
    noBtn.on('pointerdown', () => confirm.destroy());

    confirm.add([bg, msg, warning, yesBtn, yesText, noBtn, noText]);
  }

  private createTitleTruck(): void {
    // Small truck that drives back and forth on top of the title
    const titleTruck = this.add.sprite(180, 60, 'truck').setScale(0.4);
    titleTruck.setFlipX(true); // Start facing right
    titleTruck.setDepth(10); // Above the title text
    
    let movingRight = true;
    
    // Create exhaust puff effect
    const createExhaustPuff = () => {
      if (!titleTruck.active) return;
      
      const exhaustX = movingRight ? titleTruck.x - 18 : titleTruck.x + 18;
      const exhaustY = titleTruck.y + 8;
      
      const puff = this.add.circle(exhaustX, exhaustY, 2, 0x888888, 0.5);
      puff.setDepth(9);
      
      this.tweens.add({
        targets: puff,
        x: movingRight ? exhaustX - 15 : exhaustX + 15,
        y: exhaustY - 8,
        alpha: 0,
        scale: 1.5,
        duration: 500,
        ease: 'Power2',
        onComplete: () => puff.destroy()
      });
    };
    
    // Exhaust timer
    this.time.addEvent({
      delay: 250,
      callback: createExhaustPuff,
      loop: true
    });
    
    // Animate truck back and forth across the title
    this.tweens.add({
      targets: titleTruck,
      x: GAME_WIDTH - 180,
      duration: 6000,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      onYoyo: () => {
        titleTruck.setFlipX(false);
        movingRight = false;
      },
      onRepeat: () => {
        titleTruck.setFlipX(true);
        movingRight = true;
      }
    });

    // Add wheel glow effects that follow the truck
    const leftGlow = this.add.sprite(0, 0, 'wheelGlow').setScale(0.5).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.4);
    const rightGlow = this.add.sprite(0, 0, 'wheelGlow').setScale(0.5).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.4);
    leftGlow.setDepth(9);
    rightGlow.setDepth(9);

    // Update wheel glow positions
    this.time.addEvent({
      delay: 16,
      callback: () => {
        leftGlow.setPosition(titleTruck.x - 8, titleTruck.y + 10);
        rightGlow.setPosition(titleTruck.x + 8, titleTruck.y + 10);
      },
      loop: true
    });

    // Animate wheel glow
    this.tweens.add({
      targets: [leftGlow, rightGlow],
      alpha: { from: 0.3, to: 0.6 },
      scale: { from: 0.4, to: 0.6 },
      duration: 300,
      yoyo: true,
      repeat: -1,
    });
  }

  private createNeonBorder(): void {
    const graphics = this.add.graphics();
    
    // Outer glow
    graphics.lineStyle(8, 0x00ffff, 0.2);
    graphics.strokeRect(15, 15, GAME_WIDTH - 30, GAME_HEIGHT - 30);
    
    // Middle glow
    graphics.lineStyle(4, 0x00ffff, 0.4);
    graphics.strokeRect(18, 18, GAME_WIDTH - 36, GAME_HEIGHT - 36);
    
    // Inner line
    graphics.lineStyle(2, 0x00ffff, 0.8);
    graphics.strokeRect(20, 20, GAME_WIDTH - 40, GAME_HEIGHT - 40);
    
    // Corner accents
    const cornerSize = 30;
    graphics.lineStyle(3, 0xffff00, 1);
    
    // Top-left
    graphics.beginPath();
    graphics.moveTo(20, 20 + cornerSize);
    graphics.lineTo(20, 20);
    graphics.lineTo(20 + cornerSize, 20);
    graphics.strokePath();
    
    // Top-right
    graphics.beginPath();
    graphics.moveTo(GAME_WIDTH - 20 - cornerSize, 20);
    graphics.lineTo(GAME_WIDTH - 20, 20);
    graphics.lineTo(GAME_WIDTH - 20, 20 + cornerSize);
    graphics.strokePath();
    
    // Bottom-left
    graphics.beginPath();
    graphics.moveTo(20, GAME_HEIGHT - 20 - cornerSize);
    graphics.lineTo(20, GAME_HEIGHT - 20);
    graphics.lineTo(20 + cornerSize, GAME_HEIGHT - 20);
    graphics.strokePath();
    
    // Bottom-right
    graphics.beginPath();
    graphics.moveTo(GAME_WIDTH - 20 - cornerSize, GAME_HEIGHT - 20);
    graphics.lineTo(GAME_WIDTH - 20, GAME_HEIGHT - 20);
    graphics.lineTo(GAME_WIDTH - 20, GAME_HEIGHT - 20 - cornerSize);
    graphics.strokePath();
  }
}

